import { useMemo } from "react";
import { cn } from "@/lib/utils";
import tinycolor from "tinycolor2";
import { isValidCssColor } from "./color-picker/utils/colorFormatUtils";
import React from "react";

interface ColorSwatchProps {
  color: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  showBorder?: boolean;
  backgroundColor?: string;
  contrastThreshold?: number;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
  color,
  size = "sm",
  className,
  showBorder = true,
  backgroundColor = "#131519", // Default dark background
  contrastThreshold = 3,
}) => {
  // Parse the color to get the proper hex value
  const swatchColor = useMemo(() => {
    if (!color) return "#000000";

    // If it's already a hex value, use it directly
    if (color.startsWith("#")) {
      return color;
    }

    // Otherwise, try to parse it with TinyColor if it's a valid color
    if (isValidCssColor(color)) {
      const parsed = tinycolor(color);
      return parsed.toHexString();
    }

    return "#000000";
  }, [color]);

  // Check if the swatch needs a border due to low contrast
  const needsBorder = useMemo(() => {
    if (!showBorder) return false;

    const contrast = tinycolor.readability(swatchColor, backgroundColor);
    return contrast < contrastThreshold;
  }, [swatchColor, backgroundColor, contrastThreshold, showBorder]);

  // Size classes
  const sizeClasses = {
    sm: "min-w-3 min-h-3 max-w-3 max-h-3",
    md: "min-w-4 min-h-4 max-w-4 max-h-4",
    lg: "min-w-6 min-h-6 max-w-6 max-h-6",
  };

  return (
    <div
      className={cn(
        "rounded-xs shadow-inner cursor-pointer",
        sizeClasses[size],
        needsBorder && "border-solid border-[0.5px] border-white/30",
        className
      )}
      style={{ backgroundColor: swatchColor }}
    />
  );
};

export default React.memo(ColorSwatch);
